import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import AppHeader from "@/components/AppHeader";
import { Building2, Loader2 } from "lucide-react";
import { getSiteColor } from "@/lib/siteColors";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminSites() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ code_site: "", nom_site: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sites`);
      setSites(res.data);
    } catch (e) {
      toast.error("Impossible de charger les sites.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/sites`, form);
      toast.success("Site créé.");
      setOpen(false);
      setForm({ code_site: "", nom_site: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la création.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppHeader />
      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-heading font-black uppercase tracking-tight text-3xl">Gestion des sites</h1>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-site-button" className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2">
                <Building2 className="h-4 w-4" />
                Ajouter un site
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="add-site-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading uppercase">Nouveau site</DialogTitle>
              </DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="code_site">Code site</Label>
                  <Input id="code_site" data-testid="site-form-code" required value={form.code_site} onChange={(e) => setForm({ ...form, code_site: e.target.value })} />
                </div>
                <div>
                  <Label htmlFor="nom_site">Nom du site</Label>
                  <Input id="nom_site" data-testid="site-form-nom" required value={form.nom_site} onChange={(e) => setForm({ ...form, nom_site: e.target.value })} />
                </div>
                <Button type="submit" disabled={submitting} data-testid="site-form-submit" className="w-full bg-primary hover:bg-primary/90 text-primary-foreground">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Créer"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Chargement…</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="sites-grid">
            {sites.map((s) => {
              const color = getSiteColor(s.CODE_SITE);
              return (
                <div key={s.ID_SITE} data-testid={`site-card-${s.ID_SITE}`} className={`bg-card border border-border border-l-4 ${color.border} rounded-md p-4`}>
                  <p className={`font-mono text-xs uppercase ${color.text}`}>{s.CODE_SITE}</p>
                  <p className="font-heading font-semibold text-lg">{s.NOM_SITE}</p>
                  <p className="text-xs text-muted-foreground mt-1">{s.ACTIF === "Oui" ? "Actif" : "Inactif"}</p>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
